import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook for barcode scanner detection
 * Detects rapid keyboard input (typical of barcode scanners)
 * and distinguishes it from manual typing
 */
const useBarcodeScanner = ({ onScan, enabled = true, minLength = 5, maxDelay = 50 }) => {
  const [buffer, setBuffer] = useState('');
  const timeoutRef = useRef(null);
  const lastKeypressRef = useRef(null);

  const processBuffer = useCallback(() => {
    if (buffer.length >= minLength) {
      // Clean up the buffer (remove any newlines or carriage returns that scanners might add)
      const cleanedBuffer = buffer.trim().replace(/[\r\n]/g, '');

      if (cleanedBuffer.length >= minLength && onScan) {
        onScan(cleanedBuffer);
      }
    }
    setBuffer('');
  }, [buffer, minLength, onScan]);

  useEffect(() => {
    if (!enabled) {
      setBuffer('');
      return;
    }

    const handleKeyDown = (event) => {
      // Ignore if user is typing in an input/textarea (except when it's a dedicated scanner field)
      const target = event.target;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      const isScannerField = target.dataset?.scannerInput === 'true';

      if (isInputField && !isScannerField) {
        return;
      }

      // Check timing between keystrokes
      const now = Date.now();
      const timeSinceLastKey = lastKeypressRef.current ? now - lastKeypressRef.current : 0;

      // If keys are coming very fast (under maxDelay ms), it's likely a scanner
      if (timeSinceLastKey > 0 && timeSinceLastKey > maxDelay + 50) {
        // Too slow, likely manual typing - reset buffer
        setBuffer('');
      }

      lastKeypressRef.current = now;

      // Handle Enter key (many scanners send this at the end)
      if (event.key === 'Enter' && buffer.length >= minLength) {
        event.preventDefault();

        // Clear any pending timeout to prevent double processing
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        processBuffer();
        return;
      }

      // Ignore special keys (except numbers and letters)
      if (event.key.length > 1 && event.key !== 'Enter') {
        return;
      }

      // Add character to buffer
      setBuffer(prev => prev + event.key);

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout to process buffer after delay
      timeoutRef.current = setTimeout(() => {
        processBuffer();
        timeoutRef.current = null;
      }, maxDelay);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, buffer, maxDelay, minLength, processBuffer]);

  // Manual trigger function for testing
  const simulateScan = useCallback((barcode) => {
    if (onScan) {
      onScan(barcode);
    }
  }, [onScan]);

  return {
    buffer,
    simulateScan,
    enabled
  };
};

export default useBarcodeScanner;
